import React from "react";
import { NavigationSlotPortal } from "@/components/NavigationSlotContext";
import { List, FilePlus2, FilePenLine, X } from "lucide-react";

/**
 * Tab workspace di dalam satu modul.
 *
 * Tab Daftar selalu tersedia dan tidak dapat ditutup.
 * Tab form menggunakan ID stabil yang disediakan pemanggil.
 *
 * Props:
 * - tabs: [{ id, type: "list"|"new"|"edit", label, dirty? }]
 * - activeTabId
 * - onActivate(tab)
 * - onClose(tab)
 * - onRequestClose(tab), opsional; pemanggil menangani konfirmasi
 */
export default function ModuleWorkspaceTabs({
  tabs = [],
  activeTabId,
  onActivate,
  onClose,
  onRequestClose,
  layer = 2,
}) {
  const listTab = tabs.find((tab) => tab.type === "list");
  const formTabs = tabs.filter((tab) => tab.type !== "list");
  const normalizedTabs = [
    ...(listTab ? [listTab] : []),
    ...formTabs,
  ];

  function activate(tab) {
    if (!tab || tab.id === activeTabId) return;
    onActivate?.(tab);
  }

  function requestClose(event, tab) {
    event.stopPropagation();

    if (!tab || tab.type === "list") return;

    if (onRequestClose) {
      onRequestClose(tab);
      return;
    }

    if (tab.dirty) {
      const confirmed = window.confirm(
        "Form memiliki perubahan yang belum disimpan. Tutup tab ini?"
      );
      if (!confirmed) return;
    }

    onClose?.(tab);
  }

  if (!listTab) {
    return (
      <div
        role="alert"
        className="px-3 py-2 text-sm text-destructive"
      >
        Tab Daftar wajib disediakan pada workspace modul.
      </div>
    );
  }

  return (
    <NavigationSlotPortal layer={layer}>
    <div
      role="tablist"
      data-global-nav-layer="2"
      aria-label="Tab workspace modul"
      className="flex min-w-0 items-center gap-0 overflow-x-auto border-b border-slate-300 bg-slate-100 px-1"
    >
      {normalizedTabs.map((tab) => {
        const active = tab.id === activeTabId;
        const isList = tab.type === "list";
        const Icon = isList
          ? List
          : tab.type === "new"
            ? FilePlus2
            : FilePenLine;

        return (
          <div
            key={tab.id}
            role="presentation"
            className={[
              "flex shrink-0 items-center rounded-none border border-b-0",
              active
                ? "border-slate-300 bg-white"
                : "border-transparent bg-slate-100",
            ].join(" ")}
          >
            <button
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`module-panel-${encodeURIComponent(String(tab.id))}`}
              onClick={() => activate(tab)}
              className={`flex items-center gap-2 px-3 py-2 text-sm hover:bg-white ${active ? "font-semibold text-sky-800" : "text-slate-700"}`}
            >
              <Icon size={15} aria-hidden="true" />
              <span>{tab.label || (isList ? "Daftar" : "Form")}</span>
              {tab.dirty && (
                <span
                  className="text-amber-600"
                  title="Perubahan belum disimpan"
                  aria-label="Perubahan belum disimpan"
                >
                  *
                </span>
              )}
            </button>

            {!isList && (
              <button
                type="button"
                aria-label={`Tutup ${tab.label || "tab"}`}
                title="Tutup tab"
                onClick={(event) => requestClose(event, tab)}
                className="mr-1 rounded p-1 hover:bg-muted"
              >
                <X size={13} aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
    </div>
    </NavigationSlotPortal>
  );
}
